import React, { useEffect, useState } from 'react';
import { X, ShieldCheck, ArrowRight, Loader2 } from 'lucide-react';

interface RedirectModalProps {
  isOpen: boolean;
  onClose: () => void;
  storeName: string;
  redirectUrl: string;
}

export const RedirectModal: React.FC<RedirectModalProps> = ({ 
  isOpen, 
  onClose, 
  storeName, 
  redirectUrl 
}) => {
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    if (!isOpen) {
      setCountdown(5); // Reset on close
      return;
    }

    // Auto-redirect simulation
    if (countdown === 0) {
      window.open(redirectUrl, '_blank');
      onClose();
      return;
    }

    const timer = setTimeout(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [isOpen, countdown, redirectUrl, onClose]);

  const handleImmediateRedirect = () => {
    window.open(redirectUrl, '_blank');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-md rounded-xl shadow-2xl overflow-hidden flex flex-col relative animate-in zoom-in-95 duration-200">
        
        {/* Header - Dark Blue */}
        <div className="bg-[#061970] px-6 py-4 flex items-center justify-between shadow-md z-10">
          <div className="flex items-center gap-3 text-white">
            <ShieldCheck className="w-6 h-6 text-[#FF7F32]" />
            <span className="font-black italic uppercase tracking-wider text-lg">COMPRA SEGURA</span>
          </div>
          <button 
            onClick={onClose}
            className="text-white/80 hover:text-white hover:bg-white/10 p-1.5 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-8 flex flex-col items-center text-center space-y-6">
          
          {/* Logo */}
          <div className="w-24 h-24 flex items-center justify-center mb-2">
            <img 
              src="/MaxMRC1.png" 
              alt="MRC" 
              className="w-full h-full object-contain drop-shadow-md"
            />
          </div>

          {/* Text */}
          <div className="space-y-2">
            <p className="text-gray-600 text-sm leading-relaxed max-w-xs mx-auto">
              Te estamos llevando a la tienda oficial de <span className="font-black text-[#061970] uppercase">{storeName}</span> para finalizar tu compra de forma segura.
            </p>
          </div>

          {/* Spinner / Countdown */}
          <div className="flex flex-col items-center gap-3 py-2">
             <div className="relative">
                <Loader2 className="w-8 h-8 text-[#061970] animate-spin opacity-80" />
                <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-[#061970] pt-0.5">
                    {countdown}
                </span>
             </div>
             <span className="text-xs text-gray-400 font-medium italic">Redirigiendo automáticamente...</span>
          </div>

          {/* Actions */}
          <div className="w-full space-y-3 pt-2">
            <button 
              onClick={handleImmediateRedirect}
              className="w-full bg-[#FF7F32] text-white py-3.5 px-6 rounded-full font-bold uppercase italic tracking-wider flex items-center justify-center gap-2 hover:bg-[#e66a20] transition-all shadow-lg hover:shadow-xl active:scale-[0.98] group"
            >
              IR A LA TIENDA AHORA
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            
            <button 
              onClick={onClose}
              className="text-gray-400 text-xs font-bold uppercase hover:text-gray-600 transition-colors py-2 underline decoration-transparent hover:decoration-gray-300 underline-offset-4"
            >
              Cancelar
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};
